import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, Input, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { formatDate, formatPhone } from "../../shared/utils/formatters";
import { customersApi } from "./customers-api";
import { customerName } from "./customer-types";
import { useTimedNoResults } from "../../shared/hooks/useTimedNoResults";

export function CustomerListPage() {
  const { text } = useAppConfig();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const customers = useQuery({
    queryKey: ["customers", query, page],
    queryFn: () => customersApi.list(query, page)
  });
  const noSearchResultsDismissed = useTimedNoResults({
    query,
    enabled: Boolean(query.trim()),
    loading: customers.isFetching,
    ready: Boolean(customers.data),
    hasResults: Boolean(customers.data?.items.length)
  });
  return <section>
    <div className="page-title business-page-title">
      <div><Typography.Title level={2}>{text.customers}</Typography.Title><Typography.Paragraph type="secondary">{text.customerListHint}</Typography.Paragraph></div>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/customers/new")}>{text.newCustomer}</Button>
    </div>
    <Card>
      <Space.Compact className="list-search">
        <Input value={search} allowClear prefix={<SearchOutlined />} placeholder={`${text.search}…`} onChange={event => setSearch(event.target.value.toUpperCase())} onPressEnter={() => { setPage(1); setQuery(search); }} />
        <Button onClick={() => { setPage(1); setQuery(search); }}>{text.search}</Button>
      </Space.Compact>
      <Table
        rowKey="id"
        loading={customers.isLoading}
        dataSource={customers.data?.items ?? []}
        locale={{ emptyText: query && noSearchResultsDismissed ? null : text.noSearchResults }}
        onRow={row => ({ onClick: () => navigate(`/customers/${row.id}`) })}
        rowClassName="clickable-row"
        scroll={{ x: 850 }}
        pagination={{ current: page, pageSize: 20, total: customers.data?.total ?? 0, showSizeChanger: false, onChange: setPage }}
        columns={[
          { title: text.name, key: "name", render: (_, row) => <div><b>{customerName(row)}</b><small className="table-secondary">{row.email || "—"}</small></div> },
          { title: text.salutation, dataIndex: "salutation", key: "salutation", width: 90, render: value => value === "MR" ? text.mr : value === "MS" ? text.ms : "—" },
          { title: text.phone, dataIndex: "phone", key: "phone", render: formatPhone },
          { title: text.tags, key: "tags", render: (_, row) => row.tagLinks.map(link => <Tag key={link.tag.id} color={link.tag.color ?? "blue"}>{link.tag.name}</Tag>) },
          { title: text.vehicles, key: "vehicles", align: "right" as const, render: (_, row) => row._count.vehicles },
          { title: text.repairOrders, key: "ros", align: "right" as const, render: (_, row) => row._count.repairOrders },
          { title: text.time, dataIndex: "updatedAt", key: "updatedAt", render: formatDate }
        ]}
      />
    </Card>
  </section>;
}
